import { decodeHtmlEntities } from './strings';

const MIN_OPERANDS = 2;

const CALCULATED_OPERATORS = {
    DIVIDE: 'divide',
    MULTIPLY: 'multiply',
    ADD: 'add',
    SUBTRACT: 'subtract',
};

const isCohortObject = value => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

    const keys = Object.keys(value);
    if (keys.length === 0) return false;

    return keys.every(key => {
        const keyValue = value[key];
        return keyValue === null || typeof keyValue === 'number' || (keyValue && typeof keyValue === 'object');
    });
};

const isCalculatedField = value => {
    return (
        value &&
        typeof value === 'object' &&
        value.operator &&
        Array.isArray(value.fields) &&
        Object.values(CALCULATED_OPERATORS).includes(value.operator)
    );
};

const calculateFieldValue = calculatedField => {
    const { operator, fields } = calculatedField;

    if (!fields || fields.length < MIN_OPERANDS) {
        return null;
    }

    const values = fields.map(field => {
        let value = field[1];
        if (typeof value === 'string') {
            value = parseFloat(value);
        }
        return value;
    });

    const hasInvalidValue = values.some(v => typeof v !== 'number' || isNaN(v));
    if (hasInvalidValue) {
        return null;
    }

    if (operator === CALCULATED_OPERATORS.DIVIDE) {
        let result = values[0];
        for (let i = 1; i < values.length; i++) {
            if (values[i] === 0) {
                return null;
            }
            result = result / values[i];
        }
        return result;
    } else if (operator === CALCULATED_OPERATORS.MULTIPLY) {
        return values.reduce((acc, val) => acc * val, 1);
    } else if (operator === CALCULATED_OPERATORS.ADD) {
        return values.reduce((acc, val) => acc + val, 0);
    } else if (operator === CALCULATED_OPERATORS.SUBTRACT) {
        let result = values[0];
        for (let i = 1; i < values.length; i++) {
            result = result - values[i];
        }
        return result;
    }

    return null;
};

const flattenRowData = row => {
    const flattened = {};

    Object.keys(row).forEach(key => {
        const value = row[key];

        if (isCohortObject(value)) {
            Object.keys(value).forEach(cohortPeriod => {
                const cohortData = value[cohortPeriod];
                if (isCalculatedField(cohortData)) {
                    flattened[`${key}_${cohortPeriod}`] = calculateFieldValue(cohortData);
                } else {
                    flattened[`${key}_${cohortPeriod}`] = cohortData;
                }
            });
        } else if (isCalculatedField(value)) {
            flattened[key] = calculateFieldValue(value);
        } else {
            flattened[key] = value;
        }
    });

    return flattened;
};

const sortAndTranslateHeaders = (headerNames, metadata) => {
    const headersWithMetadata = headerNames.map(headerName => {
        let baseFieldName = headerName;
        let cohortPeriod = null;

        if (metadata[headerName]) {
            baseFieldName = headerName;
        } else {
            const underscoreIndex = headerName.lastIndexOf('_');
            if (underscoreIndex > 0) {
                const potentialBase = headerName.substring(0, underscoreIndex);
                const potentialCohort = headerName.substring(underscoreIndex + 1);

                const baseMetadata = metadata[potentialBase];
                if (baseMetadata && baseMetadata.columns) {
                    const cohortColumn = baseMetadata.columns.find(col => col.name === potentialCohort);
                    if (cohortColumn) {
                        baseFieldName = potentialBase;
                        cohortPeriod = potentialCohort;
                    }
                }
            }
        }

        const fieldMetadata = metadata[baseFieldName];

        if (!fieldMetadata) {
            return {
                name: headerName,
                displayName: headerName,
                sortingId: null,
                id: null,
            };
        }

        let displayName = fieldMetadata.display_name || baseFieldName;

        if (cohortPeriod && fieldMetadata.columns) {
            const cohortColumn = fieldMetadata.columns.find(col => col.name === cohortPeriod);
            if (cohortColumn && cohortColumn.display_name) {
                displayName = cohortColumn.display_name;
            } else {
                displayName = `${displayName} ${cohortPeriod}`;
            }
        } else if (cohortPeriod) {
            displayName = `${displayName} ${cohortPeriod}`;
        }

        return {
            name: headerName,
            displayName: decodeHtmlEntities(displayName),
            sortingId: fieldMetadata.sorting_id,
            id: fieldMetadata.id,
        };
    });

    return headersWithMetadata.sort((a, b) => {
        const aSortKey = (a.sortingId ?? a.id) || 999999;
        const bSortKey = (b.sortingId ?? b.id) || 999999;

        if (aSortKey !== bSortKey) {
            return aSortKey - bSortKey;
        }

        return a.displayName.localeCompare(b.displayName);
    });
};

const escapeCSVValue = value => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    if (/[,"\r\n]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
};

const convertJSONToCSV = (jsonArray, metadata = {}) => {
    if (!jsonArray || jsonArray.length === 0) return '';

    const flattenedData = jsonArray.map(row => flattenRowData(row));

    const allHeaders = new Set();
    flattenedData.forEach(row => {
        Object.keys(row).forEach(key => allHeaders.add(key));
    });

    const headers = sortAndTranslateHeaders(Array.from(allHeaders), metadata);

    const csvHeader = headers.map(header => escapeCSVValue(header.displayName)).join(',');

    const csvRows = flattenedData.map(row => headers.map(header => escapeCSVValue(row[header.name])).join(','));

    return [csvHeader, ...csvRows].join('\n');
};

export const buildCSVFromReportData = reportData => {
    try {
        if (!reportData) {
            return '';
        }

        const rawData = reportData.results;
        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
            return '';
        }

        const metadata = reportData.metadata || {};

        return convertJSONToCSV(rawData, metadata);
    } catch (error) {
        console.error('Error building CSV from report data:', error);
        return '';
    }
};

export const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    }
};
